import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const fetchPaymentHistory = createAsyncThunk(
  "payments/fetchPaymentHistory",
  async ({ page = 1, limit = 10 } = {}, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found in token.");

      const params = new URLSearchParams();
      params.set("userId", userId);
      params.set("limit", String(limit));
      params.set("page", String(page));

      const url =
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Payment/GetPaymentHistory" +
        `?${params.toString()}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          // If backend expects Bearer, change to: Authorization: `Bearer ${token}`
          Authorization: token,
        },
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        return rejectWithValue(msg);
      }

      const data = await res.json().catch(() => ({}));
      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch payment history");
    }
  }
);

const paymentsSlice = createSlice({
  name: "payments",
  initialState: {
    items: [],
    status: "idle", // idle | loading | succeeded | failed
    error: "",
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  reducers: {
    setPaymentsPage: (state, action) => {
      state.page = action.payload;
    },
    setPaymentsLimit: (state, action) => {
      state.limit = action.payload;
      state.page = 1;
    },
    clearPaymentsError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.status = "succeeded";

        const payload = action.payload || {};

        // ✅ your API shape
        const list = Array.isArray(payload.payments) ? payload.payments : [];

        state.items = list;

        // ✅ pagination/meta from your API
        const page = Number(payload.page ?? state.page ?? 1);
        const limit = Number(payload.limit ?? state.limit ?? 10);
        const total = Number(payload.total ?? list.length ?? 0);

        state.page = page;
        state.limit = limit;
        state.total = total;
        state.totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
      })

      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch payment history";
        state.items = [];
      });
  },
});

export const { setPaymentsPage, setPaymentsLimit, clearPaymentsError } =
  paymentsSlice.actions;

export default paymentsSlice.reducer;

export const selectPayments = (s) => s.payments.items;
export const selectPaymentsStatus = (s) => s.payments.status;
export const selectPaymentsError = (s) => s.payments.error;
export const selectPaymentsMeta = (s) => ({
  page: s.payments.page,
  limit: s.payments.limit,
  total: s.payments.total,
  totalPages: s.payments.totalPages,
});
